import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Home, Users, Calendar, Share2, Image as ImageIcon, Languages, Search, KeyRound,
  GraduationCap, Printer, Link2, Check,
} from "lucide-react";

// ── Currency label for all editable prices on this page ──
const CURRENCY = "CAD";

// ── Editable budget fields: fetched from / saved to the backend so every visitor sees the same values ──
const DEFAULT_BUDGET = {
  websiteTotal: "", careMonthly: "",
  flnName: "", flnTitle: "", flnDate: "", flnSignature: "",
  bvsName: "", bvsTitle: "", bvsDate: "", bvsSignature: "",
};
// Maps local camelCase state keys to the backend's snake_case field names.
const BUDGET_FIELD_MAP = {
  websiteTotal: "website_total", careMonthly: "care_monthly",
  flnName: "fln_name", flnTitle: "fln_title", flnDate: "fln_date", flnSignature: "fln_signature",
  bvsName: "bvs_name", bvsTitle: "bvs_title", bvsDate: "bvs_date", bvsSignature: "bvs_signature",
};

// ── Shared assets / design tokens (duplicated from Proposal.jsx by design - /proposal is left untouched) ──
const IMG = {
  hero: "https://cdn.gamma.app/s03ytecqnn6ry6t/bc2105db3683425c95d36926326e7b43/SLHOWrUElnxvYh_Che-a0.jpg",
  cta:  "https://cdn.gamma.app/s03ytecqnn6ry6t/d49a2197d0b84507b24970023b43d717/moeAHGTP52mnhODSu3dN6.webp",
  mosaic1: "https://cdn.gamma.app/s03ytecqnn6ry6t/321551d954f34c60bf330f93f0c67302/1Rqss5IMbcDJ-0XNX0AvL.jpg",
};
const DARK_GREEN = "#173328";
const DARK_BLUE = "#26405A";
const GOLD = "#FFC72C";

function useReveal(threshold = 0.1) {
  const ref = React.useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(20px)", transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms` }}>
      {children}
    </div>
  );
}

function Chip({ children, light = false }) {
  return (
    <span className={`inline-block text-sm font-bold uppercase tracking-widest px-4 py-1.5 mb-5 ${light ? "bg-[#FFC72C] text-black" : "bg-[#005696] text-white"}`}>
      {children}
    </span>
  );
}

// ── Editable price input: TBD pill style when empty, large price style once typed. Dashed border = editable. ──
function PriceEditable({ value, onChange, suffix, testId }) {
  const hasValue = !!value;
  return (
    <div className="flex items-center justify-center gap-3 min-h-[4.5rem]" data-testid="price-value-wrap">
      {hasValue && <span className="font-heading text-5xl sm:text-6xl text-white">{CURRENCY}</span>}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="TBD"
        aria-label={testId}
        data-testid={testId}
        className={
          hasValue
            ? "font-heading text-5xl sm:text-6xl text-white bg-transparent text-center outline-none border-b-2 border-dashed border-white/35 focus:border-[#FFC72C] hover:border-[#FFC72C] transition-colors duration-200 w-40 sm:w-48 px-1"
            : "font-heading uppercase tracking-widest text-3xl sm:text-4xl text-[#FFC72C] bg-[#FFC72C]/15 text-center outline-none rounded-full border-2 border-dashed border-[#FFC72C] focus:bg-[#FFC72C]/25 hover:bg-[#FFC72C]/20 transition-colors duration-200 px-7 py-2.5 w-36 sm:w-40 placeholder:text-[#FFC72C]"
        }
      />
      {hasValue && suffix ? <span className="text-2xl sm:text-3xl font-sans font-semibold align-middle text-white">{suffix}</span> : null}
    </div>
  );
}

function PriceCard({ label, value, onChange, suffix, note, testId }) {
  return (
    <div className="border-4 border-[#FFC72C]/50 bg-white/[0.06] p-8 sm:p-10 text-center max-w-md mx-auto" data-testid="price-card">
      <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-3">{label}</p>
      <PriceEditable value={value} onChange={onChange} suffix={suffix} testId={testId} />
      <p className="text-white/70 text-sm mt-4">{note}</p>
    </div>
  );
}

function SectionHeading({ chip, title, subtitle, dark = false }) {
  return (
    <Reveal className="text-center mb-12">
      <Chip>{chip}</Chip>
      <h2 className={`font-heading text-4xl sm:text-5xl uppercase mb-4 ${dark ? "text-white" : "text-[#0F2419]"}`}>{title}</h2>
      {subtitle && <p className={`text-lg max-w-2xl mx-auto leading-relaxed ${dark ? "text-white/80" : "text-gray-600"}`}>{subtitle}</p>}
    </Reveal>
  );
}

function TwoColTable({ leftHeader, rightHeader, rows, dark = false }) {
  return (
    <div className="max-w-4xl mx-auto overflow-hidden border-2" style={{ borderColor: dark ? "rgba(255,255,255,0.2)" : "#e5e1d8" }}>
      <div className="grid grid-cols-1 sm:grid-cols-2" style={{ backgroundColor: dark ? "rgba(255,255,255,0.08)" : "#F3F0E8" }}>
        <div className={`px-5 py-3 text-xs font-bold uppercase tracking-widest ${dark ? "text-[#FFC72C]" : "text-[#005696]"}`}>{leftHeader}</div>
        <div className={`px-5 py-3 text-xs font-bold uppercase tracking-widest border-t sm:border-t-0 sm:border-l ${dark ? "text-[#FFC72C] border-white/15" : "text-[#005696] border-gray-200"}`}>{rightHeader}</div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className={`grid grid-cols-1 sm:grid-cols-2 border-t ${dark ? "border-white/10" : "border-gray-200"}`} data-testid={`table-row-${i}`}>
          <div className={`px-5 py-4 text-base font-semibold ${dark ? "text-white" : "text-[#0F2419]"}`}>{r[0]}</div>
          <div className={`px-5 py-4 text-base border-t sm:border-t-0 sm:border-l ${dark ? "text-white/80 border-white/10" : "text-gray-700 border-gray-200"}`}>{r[1]}</div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3 - INCLUDED GRID
// ═══════════════════════════════════════════════════════════════════════════════
const INCLUDED_ITEMS = [
  { icon: Home, name: "Website Base", desc: "The complete site: home, leadership, departments, public records, consultation offices." },
  { icon: Users, name: "Community Content Management", desc: "Every department and office edits its own section." },
  { icon: Calendar, name: "Facebook Events", desc: "Events post once, appear automatically on the site." },
  { icon: Share2, name: "Social Feed", desc: "Facebook posts flow live onto froglake.ca." },
  { icon: Link2, name: "Partnerships", desc: "The partner portal connects straight to the public site." },
  { icon: ImageIcon, name: "Project Showcase", desc: "The equine centre, water treatment, geothermal, housing and capital projects, front and centre." },
  { icon: Languages, name: "Cree Language", desc: "Placed where the Nation decides, ready for translations." },
  { icon: Search, name: "Search and AI Search", desc: "Found on Google and inside AI assistants." },
  { icon: KeyRound, name: "Hub Login", desc: "One login for members, staff and partners." },
  { icon: GraduationCap, name: "Training", desc: "Hands-on, on-site training for every content owner." },
];

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 8 - PLATFORM APPLICATIONS STATUS DIAGRAM (adapted copy, static status display)
// ═══════════════════════════════════════════════════════════════════════════════
const BUDGET_APPS = [
  { key: "hub",     name: "Hub Login Framework",    status: "included" },
  { key: "partner", name: "Partner Management",     status: "included" },
  { key: "grants",  name: "Grants Management",      status: "phase2" },
  { key: "invoice", name: "Invoice and Financial",  status: "phase2" },
  { key: "econdev", name: "Economic Development",   status: "phase2" },
  { key: "housing", name: "Housing",                status: "phase2" },
  { key: "library", name: "Library and Archives",   status: "phase2" },
  { key: "equine",  name: "Equine Centre",          status: "phase2" },
];
const BUDGET_GRID = ["equine", "hub", "partner", "library", null, "grants", "housing", "econdev", "invoice"];
const BUDGET_POS = {
  equine:  [16.5, 16.5], hub:     [50, 16.5], partner: [83.5, 16.5],
  library: [16.5, 50],   center:  [50, 50],   grants:  [83.5, 50],
  housing: [16.5, 83.5], econdev: [50, 83.5], invoice: [83.5, 83.5],
};

function BudgetPlatformDiagram() {
  return (
    <div className="relative w-full aspect-square" data-testid="budget-platform-diagram">
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: "visible" }}>
        {Object.entries(BUDGET_POS).filter(([k]) => k !== "center").map(([key, [x, y]]) => {
          const app = BUDGET_APPS.find((a) => a.key === key);
          const included = app.status === "included";
          return (
            <line key={key} x1={50} y1={50} x2={x} y2={y}
              stroke={included ? GOLD : "#B9B2A0"} strokeOpacity={included ? 1 : 0.5}
              strokeWidth={included ? 1.4 : 0.8} strokeDasharray={included ? "0" : "2 2"} />
          );
        })}
      </svg>
      {BUDGET_GRID.map((key, i) => {
        const isCenter = key === null;
        const [x, y] = isCenter ? BUDGET_POS.center : BUDGET_POS[key];
        const app = isCenter ? null : BUDGET_APPS.find((a) => a.key === key);
        const included = isCenter ? true : app.status === "included";
        return (
          <div key={i}
            data-testid={isCenter ? "budget-diagram-hub" : `budget-diagram-node-${key}`}
            className="absolute flex items-center justify-center text-center rounded-full font-heading uppercase"
            style={{
              left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)",
              width: isCenter ? "31%" : "27%", height: isCenter ? "31%" : "27%",
              backgroundColor: isCenter ? DARK_GREEN : included ? GOLD : "#FFFFFF",
              border: isCenter ? `2px solid ${GOLD}` : included ? `2px solid ${GOLD}` : "2px dashed #C9C2B2",
              color: isCenter ? "#FFFFFF" : included ? "#000000" : "#8A8370",
              fontSize: isCenter ? "clamp(0.7rem, 1.7vw, 1rem)" : "clamp(0.6rem, 1.3vw, 0.82rem)",
              fontWeight: 800, padding: "5%", lineHeight: 1.15,
              boxShadow: included && !isCenter ? "0 10px 26px rgba(255,199,44,0.5)" : "none",
              zIndex: 10,
            }}
          >
            {!isCenter && (
              <span
                className="absolute -top-2 -right-1 flex items-center justify-center rounded-full"
                style={{
                  width: 22, height: 22,
                  backgroundColor: included ? "#1F7A3D" : "#6B6454",
                  border: "2px solid white",
                }}
                data-testid={`badge-${key}`}
              >
                {included ? <Check className="w-3 h-3 text-white" /> : <span className="text-white text-[8px] font-bold">P2</span>}
              </span>
            )}
            {isCenter ? "froglake.ca" : app.name}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 9 - SCHEDULE
// ═══════════════════════════════════════════════════════════════════════════════
const SCHEDULE = [
  { when: "Week 1",      title: "Kickoff",                      desc: "Access, content owners, and DNS confirmed with the Nation's IT provider." },
  { when: "Week 2",      title: "First Draft",                 desc: "The first look at the new site." },
  { when: "Week 3",      title: "Review",                       desc: "Cree language placement decided." },
  { when: "Week 5",      title: "Base Live",                   desc: "Website base is live, editors are set up." },
  { when: "Weeks 6-8",   title: "Integrations",                desc: "Facebook, project showcase, partnerships and login connected." },
  { when: "Weeks 6-10",  title: "On-Site Onboarding",          desc: "Department training on-site, about three days." },
  { when: "Week 10",     title: "Complete",                     desc: "Site fully live and handed over." },
];

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 10 - CARE & GROWTH
// ═══════════════════════════════════════════════════════════════════════════════
const CARE_ITEMS = ["Updates and security", "Backups with quarterly restore test", "Monitoring", "Up to 4 hours of content help", "Editor and permission changes", "Facebook connection kept healthy", "Monthly report"];

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 11 - WHAT WE NEED
// ═══════════════════════════════════════════════════════════════════════════════
const NEEDS = [
  ["Final list of eleven departments", "Administration"],
  ["Council mandates, Chief and Council content and photos", "Council office"],
  ["Org chart", "Administration"],
  ["Public records, laws and election documents approved for publishing", "Administration"],
  ["Facebook page admin access", "Communications"],
  ["Project photos and video", "Nation or media partner"],
  ["Cree translations", "Nation"],
  ["Named approval contact for each review", "Leadership"],
];

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 12 - TERMS
// ═══════════════════════════════════════════════════════════════════════════════
const TERMS = [
  "Fixed price for the complete website as described.",
  "Care & Growth billed monthly in advance from launch.",
  "Payment: 50% on approval to begin, 50% on launch.",
  "The Nation owns all content, media and translations it supplies.",
  "Website accounts are registered in the Nation's name from day one.",
  "Changes outside scope are quoted first.",
  "Phase 2 is proposed separately.",
  "Prices valid 60 days.",
];

function PriceInline({ value }) {
  if (!value) {
    return <span className="inline-flex items-center px-3 py-0.5 rounded-full border border-dashed border-[#FFC72C] text-[#FFC72C] font-bold text-sm align-middle" data-testid="price-tbd-chip-inline">TBD</span>;
  }
  return <span className="font-bold" data-testid="care-monthly-inline-value">{CURRENCY} {value}</span>;
}

// ── Editable approval/signature field. Dashed underline = editable indicator; turns gold on focus/hover. ──
function ApprovalField({ label, value, onChange, testId }) {
  return (
    <div className="mb-5">
      <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{label}</p>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${label.toLowerCase()}`}
        aria-label={`${label} field`}
        data-testid={testId}
        className="w-full bg-transparent text-[#0F2419] text-base outline-none border-b-2 border-dashed border-gray-300 focus:border-[#FFC72C] hover:border-[#FFC72C]/70 focus:bg-[#FFC72C]/5 transition-colors duration-200 h-7 px-1"
      />
    </div>
  );
}

const ProposalBudget = () => {
  const [budget, setBudget] = useState(DEFAULT_BUDGET);
  const saveTimer = useRef(null);

  useEffect(() => { document.title = "Build Proposal | froglake.ca"; }, []);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/proposal-budget`)
      .then((res) => {
        const d = res.data || {};
        const next = {};
        Object.keys(BUDGET_FIELD_MAP).forEach((key) => { next[key] = d[BUDGET_FIELD_MAP[key]] || ""; });
        setBudget(next);
      })
      .catch((err) => console.error("Failed to load proposal budget", err));
  }, []);

  // Local state updates instantly; the shared save to the backend is debounced so every
  // keystroke doesn't fire a request. On fire, the FULL current budget snapshot is sent (not just
  // the single field that triggered the timer) so edits made to other fields within the same
  // debounce window are never dropped.
  const handleFieldChange = (field, value) => {
    setBudget((prev) => {
      const next = { ...prev, [field]: value };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        const payload = {};
        Object.keys(BUDGET_FIELD_MAP).forEach((key) => { payload[BUDGET_FIELD_MAP[key]] = next[key]; });
        axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/public/proposal-budget`, payload)
          .catch((err) => console.error("Failed to save proposal budget", err));
      }, 600);
      return next;
    });
  };

  return (
    <div className="font-sans bg-white overflow-x-hidden budget-page">
      {/* ── 1. HERO ─────────────────────────────────────────────────────────── */}
      <section className="flex flex-col overflow-hidden no-print" style={{ minHeight: "80vh" }}>
        <div className="relative flex-1 flex flex-col items-center justify-center" style={{ minHeight: "80vh" }}>
          <img src={IMG.hero} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/72 via-black/62 to-black/88" />
          <div className="relative z-10 text-center px-6 max-w-4xl w-full mx-auto">
            <h1 className="font-heading text-white uppercase leading-none tracking-tight mb-3" style={{ textShadow: "0 4px 22px rgba(0,0,0,0.8)", fontWeight: 800, fontSize: "clamp(2.2rem, calc((100vw - 3rem) / 9), 7.5rem)" }}>
              froglake.ca
            </h1>
            <h2 className="font-heading text-[#FFC72C] uppercase leading-none mb-8" style={{ textShadow: "0 3px 18px rgba(0,0,0,0.7)", fontWeight: 800, fontSize: "clamp(1.8rem, calc((100vw - 3rem) / 9 * 0.63), 4.2rem)" }}>
              Reimagined
            </h2>
            <p className="text-white text-xl sm:text-2xl max-w-xl mx-auto leading-relaxed mb-6 font-semibold" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.8)" }} data-testid="budget-subtitle">
              One website. One price. Built for the way Frog Lake works.
            </p>
            <div className="inline-flex items-center gap-2.5 px-5 sm:px-6 py-2.5 border-2 border-[#FFC72C]/70 rounded-full bg-white/10 backdrop-blur-sm max-w-[90%] mx-auto">
              <span className="w-2 h-2 rounded-full bg-[#FFC72C] shrink-0" />
              <p className="text-white text-sm sm:text-lg uppercase tracking-widest font-bold min-w-0">
                Prepared for Frog Lake Cree Nation<br />by Bear Valley Solutions Inc.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. WHAT WE HEARD ───────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 bg-white print-break">
        <div className="container mx-auto max-w-5xl">
          <SectionHeading chip="What We Heard" title="You Told Us, We Listened" />
          <TwoColTable
            leftHeader="You told us"
            rightHeader="What that means for the site"
            rows={[
              ["The current site cannot be kept up to date and every change goes through one person.", "Each department, director and the Chief and Council office updates its own section."],
              ["The Nation's projects are showcased on everyone else's sites but its own.", "The equine centre, water treatment plant, geothermal, housing and capital projects get their own pages."],
              ["Leadership, governance and departments come first.", "The first release leads with Chief and Council, mandates by year, an editable org chart and all eleven departments."],
              ["Members live on Facebook.", "Events and posts flow from Facebook to the site, and members sign in with Facebook."],
              ["The site is a tool, not a marketing toy.", "Modern, credible - the front door to the Nation's digital platform."],
            ]}
          />
        </div>
      </section>

      {/* ── 3. ONE WEBSITE, ONE PRICE ──────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 print-break" style={{ backgroundColor: DARK_GREEN }}>
        <div className="container mx-auto max-w-5xl">
          <SectionHeading chip="The Price" title="One Website, One Price" dark />
          <Reveal className="mb-12">
            <PriceCard
              label="Complete Website" value={budget.websiteTotal}
              onChange={(v) => handleFieldChange("websiteTotal", v)}
              note="CAD, fixed price, everything below included" testId="price-input-website-total"
            />
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {INCLUDED_ITEMS.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.name} delay={i * 40}>
                  <div className="group border-2 border-white/20 bg-white/[0.06] p-5 h-full transition-all duration-300 hover:border-[#FFC72C] hover:bg-white/[0.14] hover:-translate-y-1" data-testid={`included-item-${i}`}>
                    <Icon className="w-6 h-6 text-[#FFC72C] mb-3 transition-transform duration-300 group-hover:scale-110" />
                    <h3 className="font-heading text-sm uppercase text-white mb-1.5">{item.name}</h3>
                    <p className="text-white/70 text-sm leading-snug">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="text-center mt-10">
            <p className="text-white/70 text-base" data-testid="care-monthly-line">
              Ongoing support after launch: <PriceInline value={budget.careMonthly} /> per month
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── 4. THE WEBSITE BASE ────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 bg-[#F9F7F4]">
        <div className="container mx-auto max-w-5xl">
          <SectionHeading chip="What's Included" title="The Website Base" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "Home", desc: "A scroll-driven story of the Nation, current projects, news, and full site navigation." },
              { title: "Leadership and Governance", desc: "Chief and Council with portfolios, administration, an editable org chart including Nation companies and boards, and council mandates by year showing the work delivered." },
              { title: "Departments", desc: "All eleven departments, with staff, roles, approved contacts, programs and services." },
              { title: "Public Information", desc: "BCRs, motions, minutes, laws, codes, elections and maps, published only as leadership approves." },
              { title: "Consultation Offices", desc: "On-reserve and off-reserve offices, each with its own page." },
            ].map((b, i) => (
              <Reveal key={b.title} delay={i * 50}>
                <div className="border-2 border-gray-200 bg-white p-6 h-full transition-all duration-300 hover:border-[#005696] hover:shadow-lg" data-testid={`base-block-${i}`}>
                  <h3 className="font-heading text-lg uppercase text-[#0F2419] mb-2">{b.title}</h3>
                  <p className="text-gray-600 text-base leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="text-center text-gray-500 text-sm mt-10" data-testid="base-footer-line">
              Mobile friendly, QR-code ready, hosted in Canada under OCAP principles.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── 5. YOUR COMMUNITY MANAGES ITS OWN CONTENT ──────────────────────── */}
      <section className="py-16 sm:py-20 px-4" style={{ backgroundColor: DARK_BLUE }}>
        <div className="container mx-auto max-w-5xl">
          <SectionHeading chip="Content Ownership" title="Your Community Manages Its Own Content" dark />
          <TwoColTable
            leftHeader="Who"
            rightHeader="What they update"
            dark
            rows={[
              ["Chief and Council office", "Their section, mandates and announcements"],
              ["Administration", "Organization-wide information and the org chart"],
              ["Each department director", "Their department's page, staff and programs"],
              ["Consultation offices", "Their office page and contact details"],
              ["Communications", "News, events and the Facebook connection"],
              ["Economic development", "Partnerships and the project showcase"],
            ]}
          />
          <Reveal className="max-w-3xl mx-auto mt-10">
            <ul className="space-y-2.5">
              {["Editors sign in, change their page, publish.", "Every change is logged.", "Members-only content never appears publicly.", "Directors are set up in person during on-site onboarding."].map((l, i) => (
                <li key={i} className="flex gap-2.5 text-white/85 text-base">
                  <Check className="w-4 h-4 text-[#FFC72C] shrink-0 mt-1" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ── 6. EVENTS, SOCIAL, PROJECTS AND CREE ───────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <SectionHeading chip="Community and Culture" title="Events, Social, Projects and Cree" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { icon: Calendar, title: "Facebook Events and Social Feed", desc: "Post once on Facebook, see it on froglake.ca, with controls over what shows." },
              { icon: ImageIcon, title: "Project Showcase", desc: "The equine centre, water treatment plant, geothermal, housing, capital projects, and room for the Legacy Stones Trail - told with real local voices." },
              { icon: Languages, title: "Cree Language", desc: "Placed where the Nation decides at first review. Translation supplied by the Nation." },
              { icon: Search, title: "Search and AI Search", desc: "Found on Google and in AI assistants. Fast on every device." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.title} delay={i * 60}>
                  <div className="group border-2 border-gray-200 bg-white p-7 h-full transition-all duration-300 hover:border-[#FFC72C] hover:shadow-xl hover:-translate-y-1" data-testid={`events-card-${i}`}>
                    <Icon className="w-7 h-7 text-[#005696] mb-3 transition-transform duration-300 group-hover:scale-110" />
                    <h3 className="font-heading text-lg uppercase text-[#0F2419] mb-2">{c.title}</h3>
                    <p className="text-gray-600 text-base leading-relaxed">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 7. PARTNERSHIPS AND THE LOGIN FRONT DOOR ───────────────────────── */}
      <section className="py-16 sm:py-20 px-4" style={{ backgroundColor: DARK_GREEN }}>
        <div className="container mx-auto max-w-5xl">
          <SectionHeading chip="Connections" title="Partnerships and the Login Front Door" dark />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Reveal>
              <div className="border-2 border-white/20 bg-white/[0.06] p-7 h-full" data-testid="partnerships-card">
                <Link2 className="w-7 h-7 text-[#FFC72C] mb-3" />
                <h3 className="font-heading text-xl uppercase text-white mb-3">Partnerships</h3>
                <p className="text-white/80 text-base leading-relaxed">
                  The partner portal already in use connects to the site. The public page shows a featured partner each month and the Nation's own businesses. Partner details, scoring and reports stay behind the login. A "become a partner" interest form routes to economic development for review before any application is sent.
                </p>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="border-2 border-white/20 bg-white/[0.06] p-7 h-full" data-testid="hub-login-card">
                <KeyRound className="w-7 h-7 text-[#FFC72C] mb-3" />
                <h3 className="font-heading text-xl uppercase text-white mb-3">Hub Login</h3>
                <p className="text-white/80 text-base leading-relaxed">
                  One login for members, staff and partners. Members sign in with Facebook. Staff and partners land in the areas they have access to.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 8. PLATFORM APPLICATIONS, ONE LOGIN ────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 bg-[#F9F7F4] print-break">
        <div className="container mx-auto max-w-5xl">
          <SectionHeading chip="The Platform" title="Platform Applications, One Login" subtitle="Every application connects through one login. Two are included now; the rest are scoped for Phase 2." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <Reveal>
              <div className="space-y-2.5">
                {BUDGET_APPS.map((app) => {
                  const included = app.status === "included";
                  return (
                    <div key={app.key} className={`flex items-center justify-between gap-3 px-5 py-4 border-2 ${included ? "bg-[#FFC72C]/15 border-[#FFC72C]" : "bg-white border-gray-200"}`} data-testid={`budget-app-row-${app.key}`}>
                      <span className={`text-base font-semibold ${included ? "text-[#0F2419]" : "text-gray-500"}`}>{app.name}</span>
                      {included ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#1F7A3D] bg-[#1F7A3D]/10 px-2.5 py-1 rounded-full shrink-0">
                          <Check className="w-3 h-3" /> Included now
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full shrink-0">Phase 2</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </Reveal>
            <Reveal delay={150}>
              <BudgetPlatformDiagram />
            </Reveal>
          </div>
          <Reveal className="flex items-center justify-center gap-8 mt-10 flex-wrap">
            <span className="inline-flex items-center gap-2 text-sm text-gray-600"><span className="w-4 h-4 rounded-full bg-[#FFC72C] inline-block" /> Included now</span>
            <span className="inline-flex items-center gap-2 text-sm text-gray-600"><span className="w-4 h-4 rounded-full border-2 border-dashed border-gray-400 inline-block" /> Phase 2</span>
          </Reveal>
          <p className="text-center text-gray-500 text-sm mt-4">Phase 2 applications are scoped and priced separately with the leaders of each area.</p>
        </div>
      </section>

      {/* ── 9. SCHEDULE ─────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 print-break" style={{ backgroundColor: DARK_BLUE }}>
        <div className="container mx-auto max-w-3xl">
          <SectionHeading chip="Timeline" title="Schedule" subtitle="Weeks from approval to go-live." dark />
          <div className="relative pl-8 sm:pl-10">
            <div className="absolute left-[9px] sm:left-[11px] top-2 bottom-2 w-px bg-white/25" />
            {SCHEDULE.map((s, i) => (
              <Reveal key={i} delay={i * 50} className="relative pb-8 last:pb-0">
                <span className="absolute -left-8 sm:-left-10 top-1 w-5 h-5 rounded-full bg-[#FFC72C] border-4 border-[#26405A]" data-testid={`schedule-dot-${i}`} />
                <p className="text-[#FFC72C] text-xs font-bold uppercase tracking-widest mb-1">{s.when}</p>
                <h3 className="font-heading text-xl uppercase text-white mb-1">{s.title}</h3>
                <p className="text-white/75 text-base leading-relaxed">{s.desc}</p>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="text-center text-white/70 text-base mt-6 font-semibold" data-testid="schedule-closing-line">
              Complete well ahead of the spring election period.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── 10. CARE & GROWTH ──────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4" style={{ backgroundColor: DARK_GREEN }}>
        <div className="container mx-auto max-w-4xl">
          <SectionHeading chip="After Launch" title="Care & Growth" dark />
          <Reveal className="mb-10">
            <PriceCard
              label="Care & Growth" value={budget.careMonthly}
              onChange={(v) => handleFieldChange("careMonthly", v)}
              suffix="per month" note="Billed monthly, starting at launch" testId="price-input-care-monthly"
            />
          </Reveal>
          <Reveal>
            <div className="max-w-xl mx-auto border-2 border-white/20">
              <p className="px-5 py-3 text-xs font-bold uppercase tracking-widest text-[#FFC72C] bg-white/[0.08]">Each month</p>
              {CARE_ITEMS.map((item, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-3.5 border-t border-white/10" data-testid={`care-item-${i}`}>
                  <Check className="w-4 h-4 text-[#FFC72C] shrink-0" />
                  <span className="text-white/85 text-base">{item}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal>
            <p className="text-center text-white/60 text-sm mt-6">Larger changes are quoted before work starts.</p>
          </Reveal>
        </div>
      </section>

      {/* ── 11. WHAT WE NEED FROM FROG LAKE ────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <SectionHeading chip="Before We Start" title="What We Need From Frog Lake" />
          <TwoColTable leftHeader="Item" rightHeader="Who" rows={NEEDS} />
          <Reveal>
            <p className="text-center text-gray-500 text-sm mt-8" data-testid="needs-closing-line">
              The schedule pauses when an item is late and resumes when it arrives.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── 12. TERMS ───────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 bg-[#F9F7F4]">
        <div className="container mx-auto max-w-3xl">
          <SectionHeading chip="Terms" title="Terms" />
          <Reveal>
            <ol className="space-y-3">
              {TERMS.map((t, i) => (
                <li key={i} className="flex gap-4 text-[#0F2419] text-base leading-relaxed" data-testid={`term-${i}`}>
                  <span className="font-heading text-[#005696] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span>{t}</span>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* ── 13. NEXT STEPS AND APPROVAL ─────────────────────────────────────── */}
      <section className="relative py-16 sm:py-20 px-4 overflow-hidden print-break">
        <img src={IMG.cta} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(8, 26, 16, 0.82)" }} />
        <div className="relative z-10 container mx-auto max-w-4xl">
          <SectionHeading chip="Next Steps" title="Next Steps and Approval" dark />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
            {[
              { n: "1", title: "Review", desc: "Frog Lake Cree Nation reviews this proposal." },
              { n: "2", title: "Approve and Kick Off", desc: "Sign below, work begins." },
              { n: "3", title: "First Draft", desc: "First draft ready in two weeks." },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="border-2 border-white/25 bg-white/[0.08] p-6 text-center h-full" data-testid={`next-step-${i}`}>
                  <div className="font-heading text-3xl text-[#FFC72C] mb-2">{s.n}</div>
                  <h3 className="font-heading text-base uppercase text-white mb-1">{s.title}</h3>
                  <p className="text-white/75 text-sm">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="bg-white/95 p-6 sm:p-10" data-testid="approval-block">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                <div>
                  <h4 className="font-heading text-sm uppercase text-[#0F2419] mb-5 tracking-wide">For Frog Lake Cree Nation</h4>
                  <ApprovalField label="Name" value={budget.flnName} onChange={(v) => handleFieldChange("flnName", v)} testId="fln-name-line" />
                  <ApprovalField label="Title" value={budget.flnTitle} onChange={(v) => handleFieldChange("flnTitle", v)} testId="fln-title-line" />
                  <ApprovalField label="Date" value={budget.flnDate} onChange={(v) => handleFieldChange("flnDate", v)} testId="fln-date-line" />
                  <ApprovalField label="Signature" value={budget.flnSignature} onChange={(v) => handleFieldChange("flnSignature", v)} testId="fln-signature-line" />
                </div>
                <div>
                  <h4 className="font-heading text-sm uppercase text-[#0F2419] mb-5 tracking-wide">For Bear Valley Solutions Inc.</h4>
                  <ApprovalField label="Name" value={budget.bvsName} onChange={(v) => handleFieldChange("bvsName", v)} testId="bvs-name-line" />
                  <ApprovalField label="Title" value={budget.bvsTitle} onChange={(v) => handleFieldChange("bvsTitle", v)} testId="bvs-title-line" />
                  <ApprovalField label="Date" value={budget.bvsDate} onChange={(v) => handleFieldChange("bvsDate", v)} testId="bvs-date-line" />
                  <ApprovalField label="Signature" value={budget.bvsSignature} onChange={(v) => handleFieldChange("bvsSignature", v)} testId="bvs-signature-line" />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal className="flex justify-center mt-10 no-print">
            <button
              onClick={() => window.print()}
              data-testid="print-save-pdf-button"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#FFC72C] text-black font-bold uppercase tracking-wide text-sm hover:bg-[#FFD65C] transition-colors duration-300"
            >
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
          </Reveal>

          <p className="mt-10 text-center text-xs uppercase tracking-widest text-white/30">
            Frog Lake Cree Nation · Bear Valley Solutions Inc. · Build Proposal
          </p>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer className="bg-[#060F09] py-10 px-4 no-print">
        <div className="container mx-auto max-w-5xl text-center">
          <p className="text-white/30 text-xs uppercase tracking-widest mb-4">
            © 2026 Bear Valley Solutions Inc. · Prepared for Frog Lake Cree Nation · All Rights Reserved
          </p>
          <Link to="/proposal" data-testid="budget-footer-back-link" className="inline-flex items-center gap-1.5 text-white/45 text-sm hover:text-white/80 transition-colors">
            ← Back to Proposal
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default ProposalBudget;
